// §4.2 — the single construction point.
//
// Every kernel service is built here, once, and wired to the others through plain function
// dependencies rather than by importing one another (§4.2). That is what keeps each service
// testable on its own and the kernel dependency graph acyclic — `tests/arch/boundaries.test.ts`
// asserts both.
import { nanoid } from 'nanoid';
import {
  CALENDAR_PATH,
  ROOT_APPS,
  appCatalog,
  basename,
  dayKeyAt,
  formatOfferSize,
  isDeviceLocal,
  isUnder,
  readCalendarFile,
  trashRoot,
  userRoot,
} from '@hangar/shared';
import type {
  AcceptRule,
  AppIconSpec,
  Connection,
  FileOffer,
  MultiplayerGameId,
  Payload,
  PlaySession,
  Presence,
  Rect,
  SendFileOutcome,
  Win,
  SkinId,
  UserIdentity,
  VfsPath,
  WindowManagerPolicy,
} from '@hangar/shared';
import { generateAppIcon } from '@/assets/appicon';
import { createBus, type Bus } from './bus';
import { createKernel, serviceToken, type Kernel } from './registry';
import { createSettingsService, type SettingsBackend, type SettingsService } from './settings';
import { createServerSettingsBackend } from './settings/server';
import { createSoundService, type SoundService } from './sound';
import { createShellUiService, type ShellUiService } from './shellui';
import { createInvitesService, type InvitesService } from './shellui/invites';
import {
  OfferNoSpaceError,
  createOffersService,
  rosterCandidates,
  type OffersService,
  type OffersServiceOptions,
  type SendOutcome,
} from './offers';
import {
  createSocialService,
  type EventSourceLike,
  type SocialService,
  type SocialServiceOptions,
} from './social';
import { createChatService, type ChatService, type ChatServiceOptions } from './chat';
import { createChatNotifier } from './chat/notify';
import { createPlayService, type PlayService, type PlayServiceOptions } from './play';
import { createRemindersService, type RemindersService } from './reminders';
import { createAlarmsService, ALARMS_PATH, type AlarmsService, type AlarmsStore } from './alarms';
// §34.11 (M64.4) — calendar reminders. Beside the alarms because they are the same kind of thing:
// a schedule that has to outlive the window that entered it (D-335).
import {
  CALENDAR_WATERMARKS_PATH,
  createCalendarReminders,
  type CalendarRemindersService,
} from './calendar';
import { createWmStore, type WmStore } from './wm/store';
import { createSnapService, type SnapService } from './wm/snap';
import { createDesktopsService, type DesktopsService } from './wm/desktops';
import { createSessionService, type SessionService } from './wm/session';
import { createTabLink, openChannel, type TabLink } from './vfs/tabs';
import { NULL_POLICY } from './wm/policy';
import { createAppMountService, type AppMountService } from './apps/inprocess';
import { createFileTypeRegistry, type FileTypeRegistry } from './apps/filetypes';
import { createAppRegistry, createVfsAppsStore, type AppRegistry } from './apps/registry';
import { createHealthService, headProbe, type HealthService } from './apps/health';
import { createResumeService, type ResumeService } from './apps/resume';
import { createMenuService, type MenuService } from './apps/menus';
import { createTransferTokens, type TransferTokens } from './transfer';
import { createClipboardService, type ClipboardService } from './transfer/clipboard';
import { wirePayloadOut } from './transfer/wire';
import { createDragService, type DragService } from './transfer/drag';
import { createNotifyService, type NotifyService } from './notify';
// `logging/`, not `logs/` — the monorepo's root .gitignore excludes `logs/` at any depth, so a
// directory by that name is silently untracked and the image build cannot resolve it (D-192).
import { createLogsService, type LogsService } from './logging';
import { createSearchService, type SearchEntry, type SearchService } from './search';
import { appEntries, fileEntry, indexable, settingEntries, walkFiles, windowEntries } from './search/sources';
import { acceptsPayload } from '@hangar/shared';
import { createBridgeHost, type BridgeHost } from './bridge/host';
import { createBridgeRouter, type BridgeRouter } from './bridge/router';
import { attachBridgeEnv } from './bridge/env';
import { createAppLaunchService, type AppLaunchService } from './apps/launch';
import { createApiHost } from './api/host';
import { createInprocApi } from './api/inproc';
import type { HangarApiHost } from './api/port';
import { createKeyboardService, type HostPlatform, type KeyboardService } from './keyboard';
import { createOpfsStore, type OpfsStore } from './vfs/opfs';
import { createVfsTree, type VfsTree } from './vfs/tree';
import { createVersionStore, type VersionStore } from './vfs/versions';
import { createTrashService, type TrashService } from './vfs/trash';
import { createDesktopService, type DesktopService } from './desktop';
import { createQuotaService, type QuotaService } from './vfs/quota';
import { createExportService, type ExportService } from './vfs/export';
import { createSyncService, type SyncService } from './vfs/sync-service';
import { createExecService, type ExecService } from './exec';
import { createSyncClient, type SyncClient } from './vfs/sync-client';
import { createSyncEvents, type SyncEvents } from './vfs/sync-events';
import { createFetchSyncTransport, sha256Hex } from './vfs/sync-transport';
import { createOpfsSyncState, createVfsSyncFiles } from './vfs/sync-state';
import { createVfsGateway, type VfsGateway } from './vfs/gateway';
import { browserCapabilities, type BrowserCapabilities } from './capabilities';
import type { ExecFrame } from '@hangar/shared';
import { COPY } from '@/copy';
import {
  NOTES_CAPTURE_WINDOW,
  SNIP_DIR,
  TAB_CHANNEL,
  VOICE_MEMO_DIR,
  WB_CAL_REMINDER_HORIZON_DAYS,
} from '@/config';
// §34.4 (M58.2) — the capture card's window shape and the payload that says it is one.
import { NOTES_CAPTURE_MIME } from '@hangar/shared';
import { centre } from './wm/geometry';

export const BUS = serviceToken<Bus>('bus');
export const SETTINGS = serviceToken<SettingsService>('settings');
export const SOUND = serviceToken<SoundService>('sound');
export const SHELLUI = serviceToken<ShellUiService>('shellui');
export const INVITES = serviceToken<InvitesService>('invites');
export const SOCIAL = serviceToken<SocialService>('social');
export const CHAT = serviceToken<ChatService>('chat');
export const PLAY = serviceToken<PlayService>('play');
export const ALARMS = serviceToken<AlarmsService>('alarms');
export const REMINDERS = serviceToken<RemindersService>('reminders');
export const WM = serviceToken<WmStore>('wm');
export const APPS = serviceToken<AppMountService>('apps');
export const FILETYPES = serviceToken<FileTypeRegistry>('filetypes');
export const LAUNCH = serviceToken<AppLaunchService>('launch');
export const KEYBOARD = serviceToken<KeyboardService>('keyboard');
export const OPFS = serviceToken<OpfsStore>('opfs');
export const VFS = serviceToken<VfsTree>('vfs');
export const VERSIONS = serviceToken<VersionStore>('versions');
export const TRASH = serviceToken<TrashService>('trash');
export const QUOTA = serviceToken<QuotaService>('quota');
export const EXPORTS = serviceToken<ExportService>('exports');
export const VFS_GATEWAY = serviceToken<VfsGateway>('vfsGateway');
export const SNAP = serviceToken<SnapService>('snap');
export const DESKTOPS = serviceToken<DesktopsService>('desktops');
export const SESSION = serviceToken<SessionService>('session');
export const TABS = serviceToken<TabLink>('tabs');

/**
 * §18 F9 — how long an app gets to answer `window/closeRequested`.
 *
 * §9.3 gives the handshake a timeout and says nothing about this one. Five seconds is long enough
 * for an app to draw its own "save your work?" prompt and short enough that a wedged frame cannot
 * make the close button look broken — and running out is an *allow*, never a veto (D-119).
 */
export const CLOSE_VETO_TIMEOUT_MS = 5_000;

/** §14 — the settings file, in the kernel's own `/System` area (D-290). */
const SETTINGS_PATH = '/System/.settings.json';

/**
 * §10.3 — how long a Share waits for a freshly launched target to be able to receive.
 *
 * An external app has to load a document and complete §9.3's handshake, which §9.3 itself gives
 * three seconds; these numbers cover that with room, and running out answers `'cancelled'` rather
 * than leaving the sender's promise open.
 */
const DELIVERY_ATTEMPTS = 40;
const DELIVERY_POLL_MS = 100;

/** One line describing what is being shared. Never the content (§10.3: the picker is the shell's). */
function shareSummary(payload: Payload): string {
  const first = payload.items[0];
  if (!first) return '';
  const extra = payload.items.length > 1 ? ` +${payload.items.length - 1}` : '';
  if (first.path) return `${first.path}${extra}`;
  if (first.text) return `${first.text.replace(/\s+/g, ' ').trim().slice(0, 60)}${extra}`;
  return `${first.mime}${extra}`;
}

export interface HangarKernel {
  readonly registry: Kernel;
  readonly bus: Bus;
  readonly settings: SettingsService;
  readonly sound: SoundService;
  readonly shellui: ShellUiService;
  /** §26.4 — the invitation cards on §19.4's layer. Shell-owned; an app cannot draw one. */
  readonly invites: InvitesService;
  /** §26.2 / §26.6 — the roster, presence, and the doorbell. */
  readonly social: SocialService;
  /** §26.5 — the four chat calls. Stateless: the doorbell it answers to is `social`'s. */
  readonly chat: ChatService;
  /** §26.5 / §13 (D-345) — ask Chat to open a conversation, and hear the ask. */
  openChatThread(peer: string): void;
  onOpenChatThread(listener: (peer: string) => void): () => void;
  /** The conversation a notification asked for and nothing has answered yet. */
  pendingChatThread(): string | null;
  /** §26.7 — the six play-session calls. Stateless, like `chat`. */
  readonly play: PlayService;
  /** §34.13 — the four file-offer calls. Stateless; the doorbell it answers to is `social`'s. */
  readonly offers: OffersService;
  /**
   * §34.13 — send a file to a connection: draw the roster picker, create the offer, ring them.
   *
   * The shell does all three because only the shell can do the first one (§19.4) and because the
   * roster is §26.9's and not an app's. Explorer reaches this through `ExplorerShellAccess`.
   */
  sendFileToConnection(path: VfsPath, size: number): Promise<SendFileOutcome>;
  /** §34.13 — a card was answered. `'no-space'` leaves the card up (§34.17). */
  answerFileOffer(id: string, answer: 'accept' | 'decline'): Promise<void>;
  /**
   * §26.4 / §26.7 — start a game with a connection: create the session, open the window, ring them.
   *
   * The shell does all three because only the shell can do the middle one, and because the card the
   * peer sees is drawn on a layer no app may reach.
   */
  startPlay(gameId: MultiplayerGameId, peer: string): Promise<void>;
  /** §26.4 — a card was accepted: answer the invitation and open the game on it. */
  joinPlay(sessionId: string, gameId: string): Promise<void>;
  /**
   * §26.8 (D-586) — open a game window on a session that is **already** running.
   *
   * `joinPlay` without the `respond`: an active session has been accepted once and answering it a
   * second time is a 409. This is the verb behind both halves of resuming — Connections' list and a
   * turn-based title re-attaching itself after a reload.
   */
  resumePlay(sessionId: string, gameId: string): void;
  /** §26.8 (D-586) — every session of this user's that is still `invited` or `active`. */
  livePlay(): Promise<PlaySession[]>;
  /**
   * §26.8 (D-586) — this user's running game of `gameId`, or `null`.
   *
   * `active` only, and the newest if somehow there are two. An `invited` session has a card (§26.4)
   * and a window that opens on it would be a game the server refuses every move for.
   */
  activePlay(gameId: string): Promise<PlaySession | null>;
  /** The session a launch was asked to open on, read once. `ChatShellAccess.pendingThread`'s shape. */
  pendingPlay(): { sessionId: string; gameId: string } | null;
  onOpenPlay(listener: (request: { sessionId: string; gameId: string }) => void): () => void;
  /**
   * §11.14 — the alarm schedule and the stopwatch, owned by the **shell**.
   *
   * Here rather than in the app because an alarm that stops existing when its window closes is not
   * an alarm. It rings through §13, which is the surface that already knows how to reach somebody
   * looking at something else.
   */
  readonly alarms: AlarmsService;
  /** §34.11 — the calendar's reminder schedule, derived from `/Apps/clock/calendar.json`. */
  readonly calendarReminders: CalendarRemindersService;
  /** §34.11 — the day a clicked reminder asked to focus, and its subscription. */
  pendingCalendarDay(): string | null;
  onOpenCalendarDay(listener: (dayKey: string) => void): () => void;
  /** §34.11 — the app saved the calendar; re-arm now rather than on the next tick. */
  calendarChanged(): void;
  /** §27 — shared reminder lists and the groups they are shared with. Stateless, like `chat`. */
  readonly reminders: RemindersService;
  /**
   * §26.1 (D-220) — the signed-in user's own namespaced ref, or `null` for a guest.
   *
   * The instance half comes from the server, not from the client: `HANGAR_INSTANCE` is the
   * server's configuration and a client that guessed it would namespace rows under a name nothing
   * else uses. Until `setIdentity` carries one, this answers `null`, which renders as "you have no
   * connections" rather than as a wrong identity.
   */
  socialRef(): string | null;
  readonly wm: WmStore;
  readonly apps: AppMountService;
  /** §9.2 — extension → app, with the user's Default apps choice on top. */
  readonly filetypes: FileTypeRegistry;
  /** §9.6 — the installed external apps. */
  readonly installed: AppRegistry;
  /** §9.6 — the `HEAD` schedule that decides whether an app's icon is dimmed. */
  readonly health: HealthService;
  /** §9.6 / F10 — which minimized `resumable` windows should give their iframe back. */
  readonly resume: ResumeService;
  /** §8 — who is signed in. §11.5's paste-a-URL field reads it (D-025). */
  identity(): UserIdentity;
  /** §8.1 — the boot's answer from `GET /api/me`, or `null` for a guest. Set once, at boot. */
  setIdentity(
    session: { id: string; displayName: string; isAdmin: boolean } | null,
    /** §26.1 (D-220) — the server's `HANGAR_INSTANCE`, so the client never guesses one. */
    instance?: string | null,
  ): void;
  /** §11.6 — whether this session may change the server's anonymous-access toggle (§8.3). */
  isAdmin(): boolean;
  /** §9.3 — one `message` listener for every app frame on the desktop. */
  readonly bridge: BridgeHost;
  /** §9.3 `menu` — the tree each *window* has published, for whichever skin draws menus. */
  readonly menus: MenuService;
  /** §10 — payload-scoped capability tokens, the permission model all three transfer paths share. */
  readonly transfer: TransferTokens;
  /** §10.1 — the 25-entry ring, and the gesture that makes a read legal. */
  readonly clipboard: ClipboardService;
  /** §10.2 — a drag between windows, tracked by the shell because apps cannot see each other. */
  readonly drag: DragService;
  /** §13 — the toasts, the Center, and the do-not-disturb rule. */
  readonly notify: NotifyService;
  /** §11.13 / D-191 — the shell's own diagnostic ring, and what the Console reads. */
  readonly logs: LogsService;
  /** §12 — one index over apps, files, open windows and settings. */
  readonly search: SearchService;
  /**
   * §12 — walk the VFS and rebuild the `file` source. Called once at boot by the host; after that
   * the index follows `fs:changed` one event at a time, which is what "incrementally" means for
   * the only source large enough for the difference to matter.
   */
  reindexFiles(): Promise<void>;
  /**
   * §12 — refresh the apps, windows and settings sources. Cheap and synchronous; a launcher calls
   * it as it opens, so an app installed since the last one shows up without a reload.
   */
  reindexSearch(): void;
  /**
   * §12 — act on a search result: launch an app, open a file in its default app, or focus a
   * window. One function, because "`Enter` launches or focuses" is a single sentence and both
   * launchers have to mean the same thing by it. Returns whether anything happened.
   */
  activateSearchResult(entry: SearchEntry): boolean;
  /** §10.2 / §10.3 — what an app declared it accepts, built-in or installed. */
  acceptsFor(appId: string): readonly AcceptRule[] | undefined;
  /**
   * §9.3 `command/fire` — deliver a command id to a window, whichever transport it lives behind:
   * a menu item, a jump-list entry, or a forwarded reserved chord. Returns whether it landed.
   */
  fireCommand(instanceKey: string, commandId: string): boolean;
  /**
   * §10 — hand a payload to one window, whichever transport it lives behind. Mints the
   * payload-scoped token first, so the receiver is authorized for exactly this content and this
   * transaction. Returns whether it landed.
   *
   * The one delivery path for all three of §10's routes: the clipboard-history pick (§10.1), a
   * drop (§10.2) and Share (§10.3). Same shape as `fireCommand`, for the same reason (D-124).
   */
  deliverPayload(instanceKey: string, payload: Payload): boolean;
  /**
   * §18 F9 — close a window the way a user closes one: ask the app first, and honour a veto.
   * Resolves to whether it actually closed. `wm.close` is still the unconditional form, for a
   * decision that has already been made.
   */
  requestClose(winId: string): Promise<boolean>;
  /** What `handshake/welcome` tells an app about the desktop it woke up in (§9.3). */
  welcomeFor(appId: string): {
    skin: string;
    theme: 'light' | 'dark';
    locale: string;
    user: UserIdentity;
    reservedChords: string[];
    permissions: string[];
  };
  readonly launch: AppLaunchService;
  readonly keyboard: KeyboardService;
  /** The §7.2 byte layer. Opens the origin private file system lazily, on first use. */
  readonly opfs: OpfsStore;
  /** The §7.1 filesystem: the layout, the `/System` rule, and `watch()`. */
  readonly vfs: VfsTree;
  /** §7.5 — a version per write, thinned by the retention rule, restorable without loss. */
  readonly versions: VersionStore;
  /** §7.8 — `remove()` moves here; emptying is the one destructive operation, and it asks. */
  readonly trash: TrashService;
  /** §11.7 — what is on `/Users/<user>/Desktop`, which is the whole of what the desktop is (D-276). */
  readonly desktop: DesktopService;
  /** §7.6 — the storage readout, the 90 % warning, and the guest banner's visibility. */
  readonly quota: QuotaService;
  /** §7.7 — "Export everything", client-side. Exists for guests too; non-negotiable. */
  readonly exports: ExportService;
  /** §7.4 / §18 F1 — when sync runs, and what the tray says while it cannot. */
  readonly sync: SyncService;
  /** §9.3 / §11.2 — commands delegated to installed apps (M13.2). */
  readonly exec: ExecService;
  /**
   * §7.4 — turn sync on for a signed-in session. A guest never calls this, which is what keeps
   * `sync.status` at `disabled` and the tray quiet.
   */
  startSync(): Promise<void>;
  /** D-193 — sign-out: close the change stream and disable §7.4's polling. */
  stopSync(): void;
  /** §9.5 — the scoped filesystem `HangarAPI.fs` is wired to. */
  readonly vfsGateway: VfsGateway;
  /** D-042 — what this browser can do, probed once at boot (§7.7). */
  readonly capabilities: BrowserCapabilities;
  /** §5.4 — edge snapping, the live zone overlay, and Snap Groups. */
  readonly snap: SnapService;
  /** §5.5 — virtual desktops. Switching one is a CSS class, never a DOM move (§5.7). */
  readonly desktops: DesktopsService;
  /** §5.6 — the layout and per-app blobs, restored on boot. */
  readonly session: SessionService;
  /** §18 F16 — the link to the other tabs of this origin. */
  readonly tabs: TabLink;
  /** §5.4 — the active skin's window semantics. Set by the host on mount and on skin change. */
  policy(): WindowManagerPolicy;
  setPolicy(policy: WindowManagerPolicy): void;
  readonly apiHost: HangarApiHost;
  /** True while the shell holds the whole keyboard through the Fullscreen API (§6.4). */
  immersive(): boolean;
  /** The area a window may occupy: the viewport minus the active skin's chrome (§5.4). */
  workArea(): Rect;
  /** Set by the shell whenever the viewport or the skin's chrome insets change. */
  setWorkArea(rect: Rect): void;
  /** `theme: 'system'` collapsed against the host's preference. */
  resolvedTheme(): 'light' | 'dark';
  activeSkin(): SkinId;
  /** An app's icon: its own when it has one, otherwise generated from its id (D-043). */
  appIconSvg(appId: string, size?: number): string;
  /**
   * An app's display name — a built-in's `name`, an installed app's manifest `name`, and the id
   * itself for anything the shell cannot place. Every surface that names an app (§10.3's picker,
   * §13's Center) asks this, so an app is called one thing across the desktop.
   */
  appName(appId: string): string;
  /**
   * §4.1 — *this window's* icon. An app that called `window.setIcon` gets what it set; everything
   * else falls back to the app's. Window-scoped surfaces (the frame, the switcher, Snap Assist)
   * use this; app-scoped ones (the launcher, the desktop, a grouped taskbar entry) use the
   * app's, because a group of three windows has no single icon (D-121).
   */
  windowIconSvg(win: Win, size?: number): string;
  /**
   * §9.2 / §9.5 — open a path from a *shell* surface: Start's Recommended, Spotlight (M11), a
   * notification. Picks the app from the file-type registry unless one is named, opens the
   * window, and grants that window the file — the user's click is the gesture, exactly as a
   * picker's is. Returns the window id, or null when nothing is registered for the path.
   *
   * `HangarAPI.launch.openWith` is the *app*-facing form of the same thing and additionally
   * checks that the caller could already reach the path (§9.5). The shell needs no such check;
   * it is the thing doing the granting.
   */
  openPath(path: VfsPath, appId?: string): string | null;
  /**
   * §6.4 / §11.6 — push the user's per-binding overrides into the keyboard service.
   *
   * One writer on purpose (D-544). Two settings feed the same override table — `keymap.overrides`
   * from Settings → Keyboard, and `notes.captureChord` from §34.4 — and two callers of
   * `keyboard.setOverrides` would each erase the other's, because the table is set whole.
   */
  applyKeyOverrides(): void;
  /**
   * §34.4 — arm the Quick Notes capture chord. Returns the disarm.
   *
   * Until this is called the `notes.capture` binding resolves and fires **nothing**: the shell owns
   * the key (§9.7), the app owns the card, and this is where the two meet. Reached only through
   * `NotesShellAccess` (D-059), which is Quick Notes' single grant.
   */
  armNotesCapture(): () => void;
}

export interface HangarKernelOptions {
  settingsBackend?: SettingsBackend;
  /** §26 — substituted in tests so the social layer needs no network and no `EventSource`. */
  social?: Omit<SocialServiceOptions, 'signedIn'>;
  /** §26.5 — substituted in tests so Chat needs no network. */
  chat?: ChatServiceOptions;
  /** §27 — substituted in tests so Reminders needs no network. */
  reminders?: { fetch?: typeof globalThis.fetch };
  /** §26.7 — substituted in tests so multiplayer needs no network. */
  play?: PlayServiceOptions;
  /** §34.13 — substituted in tests so file offers need no network. */
  offers?: Omit<OffersServiceOptions, 'signedIn'>;
  /**
   * The §7.2 byte layer. Overridden in tests; production opens the real origin private file
   * system.
   *
   * This seam exists because D-290 was invisible without it: the shell's *whole* kernel had to be
   * built to see that it was passing no settings backend, and outside a browser it could not be
   * built at all. A bug in what the composition root wires up cannot be caught by a test that
   * wires something else up.
   */
  opfsStore?: OpfsStore;
  /** Overridden in tests; production reads `matchMedia`. */
  prefersDark?: () => boolean;
  initialWorkArea?: Rect;
  platform?: HostPlatform;
}

function browserPrefersDark(): boolean {
  return typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)').matches : true;
}

export function createHangarKernel(options: HangarKernelOptions = {}): HangarKernel {
  const bus = createBus();
  const registry = createKernel(bus);

  let workArea: Rect = options.initialWorkArea ?? { x: 0, y: 0, w: 1280, h: 800 };
  const prefersDark = options.prefersDark ?? browserPrefersDark;

  /**
   * §14 — where settings actually live (D-290).
   *
   * The tree is built further down, so this holds the handle and the backend below reads it at
   * call time. Assigning after construction is the same shape `apiHost` and `remoteTarget` use for
   * the same reason: a genuine cycle in the *data*, not in the modules (§4.2).
   */
  let settingsFiles: { read(path: string): Promise<string>; write(path: string, data: string): Promise<unknown> } | null =
    null;

  /**
   * §14's guest half: the device's own copy, in the kernel's `/System` area (D-290).
   *
   * Reached through `internal` for the same reason the session blob is (§5.6, D-065). Without any
   * backend at all the service falls back to `createMemoryBackend()` and **nothing survives a
   * reload** — not the wallpaper, not the skin, not the keymap.
   */
  const localSettings: SettingsBackend = {
    async load() {
      if (!settingsFiles) return {};
      try {
        return JSON.parse(await settingsFiles.read(SETTINGS_PATH)) as Record<string, unknown>;
      } catch {
        // Absent on a fresh origin, and a damaged file must not stop the desktop booting — the
        // defaults in the catalog are a complete, working configuration on their own.
        return {};
      }
    },
    async save(values) {
      await settingsFiles?.write(SETTINGS_PATH, JSON.stringify(values, null, 2));
    },
  };

  const settings = createSettingsService(bus, {
    // §14's other half (D-291): a signed-in user's settings live on the server and follow them
    // between devices. Layered *over* the local copy rather than replacing it, because §18 F1
    // says an offline session keeps working — so every save lands on disk first and is pushed
    // second, and a load that cannot reach the server keeps what this device already had.
    backend:
      options.settingsBackend ??
      createServerSettingsBackend({
        local: localSettings,
        signedIn: () => account !== null,
      }),
    saveDebounceMs: 250,
  });

  /**
   * §8 — who is signed in, and the one place the answer lives (D-173).
   *
   * `null` is a guest. The *id* is kept here and deliberately not on `UserIdentity`, which §4.1
   * gives to apps: an app learns a display name, never a user id (D-165). Everything that has to
   * know which user's space it is in — §7.1's root, §7.6's quota rule — reads `currentUser` on
   * every call rather than capturing it, because sign-in happens after the kernel is built.
   */
  let account: { id: string; displayName: string; isAdmin: boolean } | null = null;
  /** §26.1 — set alongside the account when the server tells us which instance this is. */
  let socialRef: string | null = null;
  const identity = (): UserIdentity =>
    account === null ? { kind: 'guest' } : { kind: 'user', displayName: account.displayName };
  const currentUser = (): string => account?.id ?? 'guest';

  const resolvedTheme = (): 'light' | 'dark' => {
    const preference = settings.get('theme');
    if (preference === 'system') return prefersDark() ? 'dark' : 'light';
    return preference;
  };

  const sound = createSoundService({
    preferences: () => ({
      muted: settings.get('sound.muted'),
      disabled: settings.get('sound.disabled'),
    }),
  });

  const shellui = createShellUiService();
  const invites = createInvitesService();
  // Typed once, as the seam rather than as the browser class, so the conditional below is a value
  // and not a union of two function types.
  const realEventSource: ((url: string) => EventSourceLike) | undefined =
    typeof EventSource === 'undefined'
      ? undefined
      : (url: string) => new EventSource(url, { withCredentials: true });
  // §26 — a guest reaches none of this (§26.9), so the service is told who is signed in rather
  // than being constructed conditionally: identity is not known at `buildKernel()` time (D-200).
  const social = createSocialService({
    signedIn: () => identity().kind === 'user',
    /**
     * §26.2 (D-344) — the first heartbeat says what the user last chose.
     *
     * Without this, signing in announced `online` to every connection for as long as it took the
     * shell to notice the setting — so somebody who had set themselves *Busy* yesterday came back
     * green, which is the one thing a status is for.
     */
    initialStatus: () => settings.get('presence.status'),
    /**
     * §26.6 — the real doorbell in a real browser (D-317).
     *
     * Defaulted here rather than left to the caller: without it `createSocialService` has no way
     * to open a stream and falls straight to §26.8 F10's 60-second poll — which works, which is
     * exactly why the omission is invisible. The presence dot would just be up to a minute stale
     * and an invitation would arrive a minute after it was sent, and nothing would look broken.
     */
    ...(realEventSource === undefined ? {} : { createEventSource: realEventSource }),
    ...(options.social ?? {}),
  });
  // §26.5 — no timers and no connection of its own; the doorbell above is what wakes it.
  const chat = createChatService(options.chat ?? {});
  // §27 — the same again: fifteen calls, no cache, woken by the same doorbell.
  const reminders = createRemindersService(options.reminders ?? {});
  // §26.7 — and once more for multiplayer. The game state lives in `gamekit/net.ts`; this is the
  // six requests underneath it.
  const play = createPlayService(options.play ?? {});
  // §34.13 — and once more for file offers. Four calls, no cache, woken by the same doorbell.
  const offers = createOffersService({
    signedIn: () => identity().kind === 'user',
    ...(options.offers ?? {}),
  });
  const wm = createWmStore(bus);
  // §5.4's semantics belong to the *skin*, which the kernel may not import (§6.5). Until the
  // host mounts one, nothing snaps — which is the honest answer, not a guessed default.
  let policy: WindowManagerPolicy = NULL_POLICY;
  const snap = createSnapService({ wm, policy: () => policy, workArea: () => workArea });
  const desktops = createDesktopsService({ wm });
  const opfs = options.opfsStore ?? createOpfsStore();
  const exports_ = createExportService({ store: opfs });
  const quota = createQuotaService({
    estimate: () => opfs.estimate(),
    isGuest: () => account === null, // §7.6 — a guest's quota is the browser's, a user's is the server's.
    bus,
  });
  // §18 F16 — the link to the other tabs. Created before the tree so the tree can publish to
  // it; the tree is handed to it afterwards, which is why `remoteTarget` is late-bound.
  let remoteTarget: VfsTree | null = null;
  const tabs = createTabLink({
    tabId: nanoid(8),
    channel: openChannel(TAB_CHANNEL),
    onRemoteChange: (event) => remoteTarget?.applyRemote(event),
  });

  // vfs ⇄ versions is a cycle in the *data*, not the modules (§4.2): the tree calls a hook it
  // was handed, and the version store restores through a write it was handed.
  let versionStore: VersionStore | null = null;
  // §7.4 — the same shape, for the same reason: the sync client cannot exist until there is a
  // session to sync, and the tree is built long before that. Null is the guest case, and it is
  // what keeps "guests never sync" true at the one place a write could leak out.
  let syncClient: SyncClient | null = null;
  /** D-193 — the change stream, alive only for a signed-in session. */
  let syncEvents: SyncEvents | null = null;
  const vfs = createVfsTree({
    store: opfs,
    bus,
    // §7.1 — re-read on every call, so signing in moves the root rather than needing a rebuild.
    user: currentUser,
    onWrite: async (path, bytes) => {
      await versionStore?.record(path, bytes);
      // §7.6 — throttled inside, and silent until the session's one warning is due.
      await quota.check();
    },
    onMove: async (from, to) => {
      await versionStore?.rename(from, to);
    },
    // §7.4 steps 3–4 — the one thing that makes a local write *pushable*. `sync()` pushes by
    // walking the state map, and only a dirty entry is pushed, so a file nothing marked is a
    // file the server never hears about no matter how many runs complete.
    onLocalWrite: async (path, bytes) => {
      if (syncClient === null) return;
      syncClient.markDirty(path, await sha256Hex(bytes));
      // §7.4's fourth trigger — debounced inside, so forty saves in a minute are one run.
      sync.noteLocalWrite();
    },
    // D-194 — the same, for a folder. Its own hook because there are no bytes to hash, and
    // without it a folder was never marked dirty and so was never pushed at all.
    onLocalMkdir: (path) => {
      if (syncClient === null) return;
      syncClient.markDirectory(path);
      sync.noteLocalWrite();
    },
    // §7.8 — a hard removal. In practice this is emptying the Trash, and that is the one case
    // the server must *not* be told about: its own copy was trashed when the file was trashed
    // here, and it never held this device's trash copy to begin with. Telling it now would ask
    // it to delete a path it has never seen.
    onLocalRemove: (path) => {
      if (syncClient === null || isUnder(path, trashRoot(currentUser()))) return;
      syncClient.markDeleted(path);
      sync.noteLocalWrite();
    },
    // §7.8 — what a move means to sync depends on the Trash, which is why the tree hands the
    // decision up rather than making it.
    onLocalMove: async (from, to) => {
      if (syncClient === null) return;
      const trash = trashRoot(currentUser());
      if (isUnder(to, trash)) {
        // Into the Trash: this *is* §7.8's delete. `DELETE /api/files` makes the server trash its
        // own copy under its own naming, so neither device pushes trash contents at the other.
        syncClient.markDeleted(from);
      } else {
        // Out of the Trash (a restore) or between two ordinary paths (a rename). Either way the
        // destination is content the server does not have at that path. The source needs a
        // tombstone only if the server ever knew it — a restore's source is a trash path it
        // never saw, and asking it to delete that would 404 on every run.
        if (!isUnder(from, trash)) syncClient.markDeleted(from);
        const entry = await vfs.internal.stat(to);
        // Directories carry no bytes and §7.4's protocol has no operation for them; the files
        // inside arrive with their own paths and rebuild the shape on the far side.
        if (entry !== null && !entry.isDir) {
          syncClient.markDirty(to, await sha256Hex(await vfs.internal.read(to)));
        }
      }
      sync.noteLocalWrite();
    },
    onLocalEvent: (event) => tabs.publish(event),
  });
  remoteTarget = vfs;
  // §14 (D-290) — the settings backend declared above can reach the disk from here on. `hydrate()`
  // runs at boot, well after this, so nothing reads through a null handle.
  settingsFiles = {
    read: (path) => vfs.readText(path),
    write: (path, data) => vfs.internal.write(path, data),
  };
  const versions = createVersionStore({
    files: vfs.internal,
    writeLive: (path, bytes) => vfs.write(path, bytes),
  });
  versionStore = versions;

  const trash = createTrashService({
    files: vfs,
    // §19.4: the shell-drawn dialog, never `window.confirm` — an app cannot fake this one.
    confirm: (message) => shellui.confirm(message),
    user: () => vfs.currentUser(),
    forgetHistory: (path) => versions.forget(path),
  });

  // apps → apiHost → launch → apps is a genuine cycle in the *data*, not in the modules: the
  // mount service takes its host as a thunk so the last edge closes after construction.
  const filetypes = createFileTypeRegistry({
    defaults: () => settings.get('filetypes.defaults'),
    setDefaults: (next) => settings.set('filetypes.defaults', next),
  });

  let apiHost: HangarApiHost | null = null;
  /**
   * §9.3 — what a validated inbound message is *done with*. Assigned after construction: the router
   * needs the api host, the api host needs the launch service, and the launch service needs the
   * registry the bridge already holds. One late edge closes the loop without making the modules a
   * cycle (§4.2).
   */
  let bridgeRouter: BridgeRouter | null = null;
  const apps = createAppMountService({
    createApi: (ctx) => {
      if (!apiHost) throw new Error('kernel: api host used before construction finished');
      return createInprocApi(ctx, apiHost);
    },
    onFileTypes: (appId, types) => filetypes.register(appId, types),
    // §24.11 (D-200) — read per call, never captured: signing in or out changes what `visible()`
    // answers in the same tick, with no cache to invalidate.
    isSignedIn: () => identity().kind === 'user',
  });

  // §9.6 — installed external apps. Hydrated by the shell at boot; the launcher, the bar and the
  // file-type registry all read from here so "installed" means one thing.
  const installed = createAppRegistry({
    store: createVfsAppsStore({
      readText: (path) => vfs.readText(path),
      write: (path, data) => vfs.internal.write(path, data),
      exists: (path) => vfs.exists(path),
    }),
    fetchJson: async (url) => {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
      return response.json();
    },
    // D-189 — an installed app whose manifest shipped no icon is cached with the *semantic*
    // fallback, not an abstract mark: this icon is the one that survives the origin going down
    // (§9.6), so it is the one that most needs to say what the app is.
    generateIcon: (appId) => generateAppIcon(appId, { size: 32, ...iconInputsFor(appId) }),
    onChange: () => {
      // §9.2 — an installed app's file types reach the registry the same way a built-in's do.
      for (const app of installed.list()) {
        filetypes.register(
          app.id,
          (app.manifest.fileTypes ?? []).map((type) => ({
            ext: type.ext,
            role: type.role,
            ...(type.mime ? { mime: type.mime } : {}),
          })),
        );
      }
      bus.emit('app:registryChanged', {});
    },
  });

  // §9.6 — the health schedule. It reads and writes the registry and nothing else, so a probe is
  // never able to remove an app: only the user's Uninstall is (D-027).
  const health = createHealthService({
    installed,
    head: headProbe,
    subscribe: (onChange) => bus.on('app:registryChanged', onChange),
    onResult: (appId, healthy) => bus.emit('app:healthChanged', { appId, healthy }),
  });

  // §9.6 / F10 — the unload clock. It decides and announces; the host layer owns the iframe
  // (§6.5), so nothing here can reach one.
  const resume = createResumeService({
    bus,
    windows: () => wm.list(),
    // A built-in has no frame to unload, so `resumable` is a question only a manifest answers.
    resumable: (appId) => installed.get(appId)?.manifest.window?.resumable === true,
  });

  // §9.3 — a published menu belongs to a window instance and dies with it.
  const menus = createMenuService({
    onPublish: (instanceKey) => {
      const win = wm.list().find((w) => w.instanceKey === instanceKey);
      if (win) bus.emit('app:menuPublished', { winId: win.id });
    },
  });
  bus.on('window:closed', ({ instanceKey }) => menus.clear(instanceKey));

  // §10 — the permission model for clipboard, drag and Share alike. Nothing it holds outlives the
  // window it was handed to, so a close is a revoke.
  const transfer = createTransferTokens();
  bus.on('window:closed', ({ instanceKey }) => transfer.revoke(instanceKey));

  // §10.1 — writes are free, reads need the gesture the shell saw. The ring is session-only.
  const clipboard = createClipboardService({
    focusedInstance: () => {
      const focusedId = wm.getState().focusedId;
      return focusedId === null ? null : (wm.get(focusedId)?.instanceKey ?? null);
    },
  });

  const bridge = createBridgeHost({
    onMessage: (connection, message) => {
      bus.emit('bridge:message', { instanceKey: connection.instanceKey, key: message.key });
      bridgeRouter?.(connection, message);
    },
    onDrop: (drop) => bus.emit('bridge:drop', { reason: drop.reason }),
  });

  const launch = createAppLaunchService({
    bus,
    wm,
    apps,
    installed,
    workArea: () => workArea,
  });

  // §11.7 (D-276) — the desktop is a *folder*, and this is the view of it every skin renders.
  const desktop = createDesktopService({
    vfs,
    bus,
    // §24.11 (D-200) — `visible()`, so a shortcut to a gated game draws dimmed while signed out
    // rather than launching something a visitor is not allowed to have.
    knownApp: (appId) => apps.visible().includes(appId) || installed.has(appId),
    appName: (appId) => apps.get(appId)?.name ?? installed.get(appId)?.manifest.name ?? appId,
    // D-284 — the desktop a profile has before it has one of its own. Same `launchable()` the
    // launcher and the bar use, so the three never disagree about what exists.
    apps: () =>
      launch.launchable().map((app) => ({
        id: app.id,
        name: app.name,
        ...(app.category === undefined ? {} : { category: app.category }),
      })),
    trash: (path) => trash.trash(path),
    arcadeFolderName: COPY.desktop.arcadeFolder,
    materialized: {
      get: () => settings.get('ui.desktopArranged'),
      set: (users) => settings.set('ui.desktopArranged', [...users]),
    },
    offered: {
      get: () => settings.get('ui.desktopOfferedApps'),
      set: (next) => settings.set('ui.desktopOfferedApps', next as Record<string, string[]>),
    },
    // D-292 — `set` is debounced; the desktop's markers cannot afford to be.
    persist: () => settings.flush(),
  });

  // §5.6 — guests persist to `/System/.session.json`, which is the kernel's own area and reached
  // through `internal` for the same reason the version store is (D-065).
  const session = createSessionService({
    wm,
    store: {
      readText: (path) => vfs.readText(path),
      write: (path, data) => vfs.internal.write(path, data),
      exists: (path) => vfs.exists(path),
    },
    // §24.11 (D-200) — `visible()` rather than `get()`: a restored window naming a **gated** app
    // is skipped exactly as one naming an uninstalled app is (§18 F12), so a signed-out visitor
    // never gets a window they cannot use. Ticking back in re-restores it on the next boot.
    isInstalled: (appId) => apps.visible().includes(appId) || installed.has(appId),
    grants: {
      of: (instanceKey) => vfsGateway.grantsFor(instanceKey),
      restore: (instanceKey, paths) => vfsGateway.grant(instanceKey, [...paths]),
    },
    canWrite: () => tabs.isOwner(),
    onActive: () => tabs.claim(Date.now()),
    onSkipped: (appIds) => shellui.showToast(COPY.sessionSkipped(appIds.length)),
    onError: (error) => console.warn('session', error),
  });

  const vfsGateway = createVfsGateway({
    tree: vfs,
    versions,
    trash,
    /**
     * §9.5's "the paths its spec names in §11", per app id — used for the first time here (M62/M63).
     *
     * Two apps in §34 write into a **fixed, spec-named** folder of the user's home: Voice Memos into
     * `Documents/Voice Memos` (§34.9) and Snip into `Pictures/Screenshots` (§34.10). Neither is
     * reachable through `HangarAPI` by default — §9.5 gives an app its own `/Apps/<id>` and nothing
     * else — so without this the spec's own paths would be refused on every write.
     *
     * This is the **narrowest** of the three mechanisms available, which is why it is the one used:
     *
     *   • It is not `fileManagers`, which would hand these two all of user space and `/Apps`.
     *   • It is not D-509's `ArchiveShellAccess` shape, where the shell performs the write. Archive
     *       needs that because its directory is *dynamic* — whichever folder the zip sits in — and
     *       this option is keyed by app id, so it cannot express that. A fixed directory can be
     *       expressed here, and then every write still goes through `confine` as the app itself,
     *       with version history, sync and quota all behaving exactly as for any other file.
     *
     * A subtree, deliberately: Snip and Voice each own the folder they create, and a granted
     * directory carries its subtree (§9.5) — which is what lets a user file their own memos into
     * sub-folders without the app losing them.
     */
    extraRoots: (appId) => {
      const user = vfs.currentUser();
      if (appId === 'voice') return [`${userRoot(user)}/${VOICE_MEMO_DIR}` as VfsPath];
      if (appId === 'snip') return [`${userRoot(user)}/${SNIP_DIR}` as VfsPath];
      // §34.4 (D-547) — Quick Notes, the same mechanism for the same reason: it writes and reads
      // one fixed directory, and a capture card that saves on `Enter` has no picker by design.
      if (appId === 'notes') return [`${userRoot(user)}/Documents/Notes` as VfsPath];
      return [];
    },
    // §19.4 — the picker is drawn by the shell, above every window, so the gesture cannot have
    // been aimed at anything else.
    pickFiles: (request, appId) => shellui.pickFiles(request, appId),
    /**
     * §9.5's "the paths its spec names in §11", used for the first time here (§34.4, D-547).
     *
     * Quick Notes writes and reads **one directory** — `/Users/<user>/Documents/Notes`, which §34.4
     * chooses precisely so that sync, §7.5 versions and "Notepad opens any note" are free. Through
     * `HangarAPI.fs` alone that is impossible: §9.5 gives an app its own `/Apps/<id>` and whatever a
     * picker granted, and a capture card that saves on `Enter` has no picker by design.
     *
     * The alternatives were worse in kind rather than in degree: a picker on every note (which is
     * not "type, Enter, gone"), or notes under `/Apps/notes/` (where nobody would find them). This
     * is the narrowest thing that makes §34.4 true, and it is narrower than a capability that wrote
     * on the app's behalf, because the confinement is the gateway's own and shows up in the
     * Inspector's granted-paths view like every other root.
     */
    // §18 F4 — "never a silent failure": the app's error is not the only thing that happens.
    onQuotaExceeded: (path) => bus.emit('fs:quotaExceeded', { path }),
  });

  // §9.5 — a picker grant never outlives the window that earned it.
  bus.on('window:closed', ({ instanceKey }) => vfsGateway.revoke(instanceKey));

  // §18 F9 — the in-process half of the close veto. Both transports register here so
  // `requestClose` has one place to ask, and a window's veto dies with the window.
  const closeHandlers = new Map<string, () => Promise<'allow' | 'deny'>>();
  bus.on('window:closed', ({ instanceKey }) => closeHandlers.delete(instanceKey));

  // §10.2 / §10.3 — the in-process half of delivery. Same shape and same reason as the veto
  // register above: `deliver` has to reach both transports, so both register in one place.
  const receiveHandlers = new Map<string, (payload: Payload, token: string) => void>();
  bus.on('window:closed', ({ instanceKey }) => receiveHandlers.delete(instanceKey));

  // §13 — the in-process half of `notify/action`, registered exactly as the two above are.
  /** §11.2 — who is listening to each in-flight `exec/run`, keyed by its envelope id. */
  const execListeners = new Map<string, (frame: ExecFrame) => void>();
  let execCounter = 0;
  const mintExecId = (): string => `exec-${(execCounter += 1)}`;

  const notifyActionHandlers = new Map<string, (id: string, action: string) => void>();
  bus.on('window:closed', ({ instanceKey }) => notifyActionHandlers.delete(instanceKey));

  // D-191 — the shell's diagnostic ring. Attached to the bus here rather than by the host: a
  // kernel that is running is a kernel worth observing, and there is no moment where it should
  // be constructed but deaf. The teardown is discarded because the service outlives nothing —
  // it is disposed of exactly when the kernel is, which is when the tab closes. `attach` still
  // returns it, so a test can subscribe and unsubscribe without a kernel at all.
  const logs = createLogsService();
  logs.attach(bus);

  const notify = createNotifyService({
    bus,
    // §13 / §14 — a Settings toggle, read at each post so changing it takes effect at once.
    doNotDisturb: () => settings.get('notify.doNotDisturb'),
    /**
     * §13 — "clicking the body focuses the window that sent it".
     *
     * Restore first when it is minimized: focusing a window the user cannot see is not what they
     * asked for, and a notification is very often the reason a window is worth looking at again.
     */
    focusWindow: (instanceKey, id) => {
      // D-345 — a chat notification opens the conversation it was about, which may mean opening
      // Chat itself. §13's ordinary rule cannot: it posted as the shell, and the shell has no
      // window to focus.
      const peer = chatNotificationPeers.get(id);
      if (peer !== undefined) {
        chatNotificationPeers.delete(id);
        openChatThread(peer);
        return;
      }
      // §34.11 — "clicking focuses the day". Same shape as D-345's chat case above, and needed for the
      // same reason: a reminder posts as the *shell*, and the shell has no window for §13's ordinary
      // rule to focus.
      const day = calendarNotificationDays.get(id);
      if (day !== undefined) {
        calendarNotificationDays.delete(id);
        openCalendarDay(day);
        return;
      }
      const win = wm.list().find((candidate) => candidate.instanceKey === instanceKey);
      if (!win) return;
      if (win.state === 'minimized') wm.restore(win.id);
      wm.focus(win.id);
    },
    deliverAction: (instanceKey, id, action) => deliverNotifyAction(instanceKey, id, action),
    // §15 — fire and forget, and silent until a skin has supplied a recipe (M10.4).
    play: () => sound.play('notify'),
  });

  /**
   * §18 F3 / D-045 — "a notification names both". One handler, reached two ways: the sync service
   * calls it directly, and `fs:conflict` on the bus reaches it too — the event existed with
   * nobody listening, which is a promise the bus was making and nothing was keeping (M14.6).
   */
  const announceConflict = (path: string, copy: string): void => {
    notify.post(
      { title: COPY.syncConflictTitle, body: COPY.syncConflictBody(path, copy) },
      { appId: 'explorer', instanceKey: 'shell' },
      Date.now(),
    );
  };
  bus.on('fs:conflict', ({ path, copyPath }) => announceConflict(path, copyPath));

  const sync = createSyncService({
    // §13 / D-045 — "A notification is posted". The service raises the conflict; §13 words it.
    onConflicts: (conflicts) => {
      for (const conflict of conflicts) announceConflict(conflict.path, conflict.copy);
    },
  });

  /**
   * §26.5 / §13 (D-338) — a chat message reaches somebody whose Chat window is shut.
   *
   * The doorbell's only listener used to be the app, so a closed app meant silence. This is the
   * listener that is always there: on a `chat` frame it makes the same `GET` the app would and
   * posts §13 for anything new. Skipped while the Chat window is focused — a message in the thread
   * you are reading is already on your screen.
   */
  /**
   * §13 (D-345) — which conversation each chat notification was about.
   *
   * §13's own click behaviour is "focus the window that sent it", and the window that sent this one
   * is the *shell* — there may be no Chat window at all, which is exactly the case D-338 added the
   * notification for. So the id is remembered here, and `activate` below turns it into "open Chat
   * on that thread" instead of into nothing.
   */
  const chatNotificationPeers = new Map<string, string>();

  /**
   * §26.5 (D-345) — "open Chat on this conversation", as a request the app answers.
   *
   * A request rather than a prop, because the window may not exist yet: `launch` opens or focuses
   * it, and the app subscribes on mount, so the peer has to survive the gap between the two. The
   * *last* request is kept rather than a queue — a second click before the first has been answered
   * means "that one", not "both".
   */
  /**
   * §34.11 — which day each reminder notification belongs to, and the pending request a click makes.
   *
   * The same shape as the chat thread below, for the same reason: `launch` opens or focuses the Clock
   * window, and the app subscribes on mount, so the day has to survive the gap between the two.
   */
  const calendarNotificationDays = new Map<string, string>();
  const calendarDayListeners = new Set<(dayKey: string) => void>();
  let pendingCalendarDay: string | null = null;
  const openCalendarDay = (dayKey: string): void => {
    pendingCalendarDay = dayKey;
    launch.launch('clock');
    for (const listener of [...calendarDayListeners]) {
      try {
        listener(dayKey);
      } catch {
        // A listener mid-unmount; the next mount takes the pending day.
      }
    }
  };

  const chatThreadListeners = new Set<(peer: string) => void>();
  let pendingChatThread: string | null = null;
  const openChatThread = (peer: string): void => {
    pendingChatThread = peer;
    launch.launch('chat');
    for (const listener of [...chatThreadListeners]) {
      try {
        listener(peer);
      } catch {
        // A listener that throws is a component mid-unmount; the next mount takes the pending peer.
      }
    }
  };
  const chatNotifier = createChatNotifier({
    threads: () => chat.threads(),
    post: (input, peer) => {
      const id = notify.post(input, { appId: 'chat', instanceKey: 'shell' }, Date.now());
      chatNotificationPeers.set(id, peer);
    },
    chatIsFocused: () => {
      const focusedId = wm.getState().focusedId;
      if (focusedId === null) return false;
      const win = wm.get(focusedId);
      return win !== undefined && win.appId === 'chat' && win.state !== 'minimized';
    },
    signedIn: () => identity().kind === 'user',
  });
  social.subscribe((event) => {
    if (event.kind === 'chat') void chatNotifier.announce();
    if (event.kind === 'invite') void refreshInvites();
    // §34.13 — an offer arrived, or one we sent was answered. The frame says *that*; `refreshOffers`
    // is what finds out which, from the one code path a cold load also takes (D-217).
    if (event.kind === 'file_offer') void refreshOffers();
  });

  /**
   * §26.4 / §26.7 — "open this game on this session", the play half of `openChatThread`.
   *
   * The same request shape and for the same reason: `launch` opens or focuses the window, and the
   * view subscribes on mount, so the session has to survive the gap between the two. The last
   * request wins — two cards accepted in quick succession means the second one, not both, because
   * a title is `multiWindow: false` and there is one window to put a game in.
   */
  const playListeners = new Set<(request: { sessionId: string; gameId: string }) => void>();
  let pendingPlay: { sessionId: string; gameId: string } | null = null;
  const openPlay = (sessionId: string, gameId: string): void => {
    pendingPlay = { sessionId, gameId };
    launch.launch(gameId);
    for (const listener of [...playListeners]) {
      try {
        listener({ sessionId, gameId });
      } catch {
        // A listener mid-unmount. The next mount reads `pendingPlay`, which is what it is for.
      }
    }
  };

  /**
   * §26.8 (D-586) — every session still `invited` or `active`, or nothing.
   *
   * The one read behind three callers — the invitation cards, Connections' resume list, and a
   * turn-based title looking for the game it was in before the page reloaded. A failure answers an
   * empty list rather than throwing, which is §26.8 F10's instinct and the right one here: "nothing
   * to resume" is a survivable wrong answer and an exception thrown at a mounting game window is not.
   */
  const livePlaySessions = async (): Promise<PlaySession[]> => {
    if (identity().kind !== 'user') return [];
    return play.live().catch(() => []);
  };

  /**
   * §26.4 — the cards, rebuilt from the server rather than accumulated from frames.
   *
   * A frame says *that* an invitation changed (D-217), so this reads every live session and makes
   * the card stack match: offered where we are the invited guest, withdrawn everywhere else. That
   * covers all four ways a card should disappear — accepted, declined, cancelled, expired — with
   * one code path instead of four, and it is also the reconnect story: a reload while an invitation
   * is open loses the stack, and the first read after sign-in puts it back (D-351).
   */
  const refreshInvites = async (): Promise<void> => {
    if (identity().kind !== 'user') return;
    let sessions: PlaySession[];
    try {
      sessions = await play.live();
    } catch (error: unknown) {
      // §26.8 F10's instinct: a social feature that cannot reach the server degrades quietly. The
      // cards already on screen stay; a stale one fails when pressed, which is visible and rare.
      logs.log({
        source: 'shell',
        level: 'warn',
        message: 'could not read play sessions',
        detail: String(error),
      });
      return;
    }
    const me = socialRef;
    const live = new Set<string>();
    for (const session of sessions) {
      // Only the *guest* of an unanswered invitation has a card. The host has the game window,
      // which is the thing the card would take them to.
      if (session.state !== 'invited' || session.guest !== me) continue;
      live.add(session.id);
      invites.offer({
        sessionId: session.id,
        from: session.host,
        fromName: session.hostName,
        gameId: session.gameId,
        gameName: appNameOf(session.gameId),
      });
    }
    for (const card of invites.store.getState().cards) {
      if (!live.has(card.sessionId)) invites.withdraw(card.sessionId);
    }
  };

  /**
   * §34.13 — the offer cards and the sender's notices, rebuilt from the server (D-217's shape).
   *
   * Exactly `refreshInvites`' design, for exactly its reasons: one read makes the card stack match
   * what the server says is live, so accepted, declined and **expired** all disappear through one
   * code path instead of three, and a reload that lost the stack gets it back on the next read.
   *
   * The `resolved` half is different in one way worth stating: it is a **take**, not a read (the
   * route marks each notice announced as it hands it over), so this must post every one it receives.
   * Dropping one loses it for good — which is why the §13 post is inside the loop and not behind any
   * condition, and why an offer that expired never appears in the list at all (§34.17: the sender is
   * told nothing about silence).
   */
  const refreshOffers = async (): Promise<void> => {
    if (identity().kind !== 'user') return;
    let list: { incoming: FileOffer[]; resolved: FileOffer[] };
    try {
      list = await offers.list();
    } catch (error: unknown) {
      // §26.8 F10's instinct again: a social feature that cannot reach the server degrades quietly.
      logs.log({
        source: 'shell',
        level: 'warn',
        message: 'could not read file offers',
        detail: String(error),
      });
      return;
    }

    const live = new Set<string>();
    for (const offer of list.incoming) {
      live.add(offer.id);
      invites.offerFile({
        id: offer.id,
        from: offer.from,
        fromName: offer.fromName,
        name: offer.name,
        size: offer.size,
      });
    }
    for (const card of invites.store.getState().offers) {
      if (!live.has(card.id)) invites.withdrawFile(card.id);
    }

    // §34.13 — "Accept/decline both toast the sender's next session via the standard §13 path". The
    // §13 path and not the card layer: this is news about something that already happened, which is
    // precisely what §26.4 says a card is *not* for.
    for (const offer of list.resolved) {
      notify.post(
        {
          title: COPY.offers.resolvedTitle,
          body:
            offer.status === 'accepted'
              ? COPY.offers.theyAccepted(offer.toName, offer.name)
              : COPY.offers.theyDeclined(offer.toName, offer.name),
        },
        { appId: 'explorer', instanceKey: 'shell' },
        Date.now(),
      );
    }
  };

  /**
   * §11.14 — the alarm schedule, and how a ring reaches somebody.
   *
   * Built here rather than in the app because the app's window is exactly the thing an alarm has
   * to outlive. It posts as `alarms` with `instanceKey: 'shell'` — the same shape the sync
   * conflict above uses — so §13 badges the app's icon and the toast survives the app never
   * having been opened.
   *
   * The file goes through the **internal** tree: `/Apps` is not the user's own space and the
   * `HangarAPI` gateway would refuse it, which is the right refusal for an app and the wrong one
   * for the shell writing on its behalf.
   */
  const alarmsStore: AlarmsStore = {
    read: async () => {
      const text = await vfs.readText(ALARMS_PATH);
      return JSON.parse(text) as unknown;
    },
    write: async (value) => {
      await vfs.internal.write(ALARMS_PATH, JSON.stringify(value, null, 2));
    },
  };
  const alarms = createAlarmsService({
    store: alarmsStore,
    announce: (alarm, lateMs) => {
      notify.post(
        {
          title: COPY.alarms.ringingTitle(alarm.label),
          body: lateMs >= 60_000 ? COPY.alarms.ringingLate(lateMs) : COPY.alarms.ringingBody(alarm.hour, alarm.minute),
        },
        { appId: 'alarms', instanceKey: 'shell' },
        Date.now(),
      );
      // §15 — the same cue §13 uses. An alarm nobody hears is a notification, not an alarm, and
      // the sound settings are the one place a user has already said whether they want one.
      sound.play('notify');
    },
  });

  /**
   * §34.11 (M64.4) — the calendar's reminders.
   *
   * The watermarks share the alarms file rather than opening a second one: both are "schedule state
   * the shell owns", both are written from the same tick, and a second file would be a second thing
   * that can be half-written. The events themselves are **not** in it — they are the app's own file
   * under `/Apps/clock/`, which is where the user's data belongs and which syncs (§7.4).
   */
  const calendarReminders = createCalendarReminders({
    readEvents: async () => {
      const text = await vfs.readText(CALENDAR_PATH);
      return readCalendarFile(JSON.parse(text) as unknown).events;
    },
    watermarks: {
      read: async () => {
        const text = await vfs.readText(CALENDAR_WATERMARKS_PATH);
        return JSON.parse(text) as unknown;
      },
      write: async (value) => {
        await vfs.internal.write(CALENDAR_WATERMARKS_PATH, JSON.stringify(value, null, 2));
      },
    },
    horizonDays: WB_CAL_REMINDER_HORIZON_DAYS,
    announce: (due) => {
      const startsAt = new Date(due.startsAt);
      const id = notify.post(
        {
          title: COPY.calendar.reminderTitle(due.event.title),
          body: due.event.allDay
            ? COPY.calendar.allDayBody(
                startsAt.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }),
              )
            : COPY.calendar.reminderBody(
                startsAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
              ),
        },
        { appId: 'clock', instanceKey: 'shell' },
        Date.now(),
      );
      // §34.11 — "clicking focuses the day". The day is remembered against the notification's id, so
      // the click knows which one it was even after several have stacked up in the Center.
      calendarNotificationDays.set(id, dayKeyAt(due.startsAt));
      sound.play('notify');
    },
  });

  apiHost = createApiHost({
    wm,
    vfs: vfsGateway,
    clipboard,
    notify,
    onNotifyAction: (instanceKey, cb) => notifyActionHandlers.set(instanceKey, cb),
    onReceive: (instanceKey, cb) => receiveHandlers.set(instanceKey, cb),
    share: (ctx, payload) => sharePayload(payload, ctx.appId),
    // §4.1 (D-540) — `launch.app`'s long-declared `arg`, delivered on the same waiting path Share
    // uses. The wait is the whole difficulty: the window exists, the app has not mounted yet.
    deliverArg: (instanceKey, payload) => deliverWhenReady(instanceKey, payload),
    settings,
    shellui,
    launch,
    filetypes,
    session,
    menus,
    onCloseRequested: (instanceKey, cb) => closeHandlers.set(instanceKey, cb),
    theme: resolvedTheme,
    user: identity,
  });

  /**
   * §18 F9 — close a window the way a *user* closes one: ask first.
   *
   * An external app is asked over §9.3 (`window/closeRequested`, answered with `window/closeReply`);
   * a built-in is asked through the callback it registered with `onCloseRequested`. An app that has
   * registered nothing, that is not bridged, that throws, or that does not answer in time **loses
   * the veto** — a window the user asked to close must close (D-119).
   *
   * `wm.close` remains the unconditional form, and is what the shell uses when the decision has
   * already been made: the app closing itself, an uninstall, session teardown.
   */
  async function requestClose(winId: string): Promise<boolean> {
    const win = wm.get(winId);
    if (!win) return false;

    let decision: 'allow' | 'deny' = 'allow';
    const connection = bridge.get(win.instanceKey);
    const handler = closeHandlers.get(win.instanceKey);
    try {
      if (connection && connection.state === 'bridged') {
        const reply = await connection.request('window/closeRequested', {}, CLOSE_VETO_TIMEOUT_MS);
        if ((reply.payload as { decision?: string }).decision === 'deny') decision = 'deny';
      } else if (handler) {
        decision = await handler();
      }
    } catch {
      // A veto has to be *given*, not merely not-refused.
      decision = 'allow';
    }

    if (decision === 'deny') return false;
    // The window may have closed while we were asking — `close` on a gone id is a no-op.
    wm.close(winId);
    return true;
  }

  /**
   * §9.3 `command/fire` — one delivery path for both transports, so a menu item, a jump-list entry
   * and a forwarded chord all reach an app the same way whichever kind of app it is.
   */
  function deliverPayload(instanceKey: string, payload: Payload): boolean {
    const token = transfer.mint(payload, instanceKey, Date.now());
    const connection = bridge.get(instanceKey);
    if (connection && connection.state === 'bridged') {
      // §9.3 — `bytes` travels base64, encoded by the one module both ends of the wire call.
      return connection.send('transfer/receive', {
        payload: wirePayloadOut(payload),
        payloadToken: token,
      });
    }
    const handler = receiveHandlers.get(instanceKey);
    if (!handler) return false;
    handler(payload, token);
    return true;
  }

  /**
   * §10.2 / §10.3 — one question, asked the same way of a built-in and an installed app. A
   * built-in declares `accepts` on its `InProcessApp`; an installed one declares it in its
   * manifest; neither is the shell's business beyond this line.
   */
  /**
   * D-188 / D-189 — everything the generator's ladder needs about one app, from the two places
   * an app can have said it: its `InProcessApp` if it is a built-in, its catalog entry if it is
   * one of §11.5's cards.
   *
   * The `words` are not a new thing an app has to declare — a built-in's `keywords` already
   * exist for §12's search and a catalog `category` already exists for the card. The id goes in
   * last: `terminal` and `photos` name themselves, so an app that says nothing else still gets
   * one chance to be recognised by what it is called.
   */
  function iconInputsFor(appId: string): { spec: AppIconSpec | undefined; words: string[] } {
    const builtin = apps.get(appId);
    // `true` includes the D-049 fixtures: an id that is not in a production catalog simply
    // misses, and asking the host whether this is localhost to decide the *colour of an icon*
    // would be a dependency bought for nothing.
    const entry = appCatalog(true).find((candidate) => candidate.id === appId);
    return {
      spec: builtin?.icon ?? entry?.icon,
      words: [...(builtin?.keywords ?? []), ...(entry ? [entry.category] : []), ...appId.split('-')],
    };
  }

  /**
   * An app's icon as an `<svg>` string. Shared by the Share picker and the kernel's own
   * `appIconSvg`, so the picker shows exactly what the launcher shows (D-115).
   *
   * The order is: an installed app's *own* icon from its origin, then whatever the generator's
   * ladder makes of what the app has declared (D-188) or said (D-189). An installed app reaches
   * the ladder too — `registry.ts` caches a generated icon for a manifest that shipped none, and
   * a catalog `category` is a better answer for that app than an abstract mark.
   */
  function appIconOf(appId: string, size = 32): string {
    const external = installed.get(appId);
    if (external && /^https?:/.test(external.icon)) {
      const href = external.icon.replace(/"/g, '&quot;');
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}"><image href="${href}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid meet"/></svg>`;
    }
    if (external) return external.icon;
    return generateAppIcon(appId, { size, ...iconInputsFor(appId) });
  }

  function appNameOf(appId: string): string {
    return apps.get(appId)?.name ?? installed.get(appId)?.manifest.name ?? appId;
  }

  function acceptsFor(appId: string): readonly AcceptRule[] | undefined {
    return apps.get(appId)?.accepts ?? installed.get(appId)?.manifest.accepts;
  }

  const drag = createDragService({
    windowAt: (winId) => {
      const win = wm.get(winId);
      return win ? { appId: win.appId, instanceKey: win.instanceKey } : undefined;
    },
    acceptsFor,
    deliver: (instanceKey, payload) => deliverPayload(instanceKey, payload),
  });
  // A window that closes mid-drag cannot be the target of the drop that follows.
  bus.on('window:closed', ({ id }) => {
    if (drag.state()?.overWinId === id) drag.hover(null);
  });

  /**
   * §10.3 — Share / Send-to.
   *
   * "→ the shell draws a picker of apps whose `accepts` matches any item's mime → launches or
   * focuses the target → delivers. Returns `'delivered'` or `'cancelled'`. **The sender learns
   * nothing else.**"
   *
   * Distinct from "Open with" (§9.2), which hands an app a *path already on disk* and grants that
   * window the file. Share hands over the content itself, under a §10 token, and grants nothing.
   */
  async function sharePayload(
    payload: Payload,
    fromAppId?: string,
  ): Promise<'delivered' | 'cancelled'> {
    const open = wm.list();
    const candidates = [...apps.visible(), ...installed.list().map((app) => app.id)]
      // An app cannot share with itself: the picker is for handing something *on*.
      .filter((appId) => appId !== fromAppId)
      .filter((appId) => acceptsPayload(acceptsFor(appId), payload))
      .map((appId) => ({
        appId,
        name: appNameOf(appId),
        icon: appIconOf(appId, 32),
        running: open.some((win) => win.appId === appId),
      }));

    if (candidates.length === 0) {
      await shellui.alert(COPY.shareNoTargets, fromAppId);
      return 'cancelled';
    }

    const chosen = await shellui.pickShareTarget(
      { summary: shareSummary(payload), candidates },
      fromAppId,
    );
    if (chosen === null) return 'cancelled';

    // Launch **or focus** (§10.3): a running app is brought forward rather than duplicated.
    const existing = open.find((win) => win.appId === chosen);
    let target = existing?.instanceKey;
    if (existing) {
      wm.focus(existing.id);
    } else {
      const winId = launch.launch(chosen);
      target = winId === null ? undefined : (wm.get(winId)?.instanceKey ?? undefined);
    }
    if (target === undefined) return 'cancelled';

    // A newly launched external app has no frame yet, let alone a handshake. Delivery waits for
    // the connection rather than firing into a window that cannot hear it.
    const landed = await deliverWhenReady(target, payload);
    return landed ? 'delivered' : 'cancelled';
  }

  /**
   * Hand a payload over as soon as the receiver can take it.
   *
   * An in-process app is ready the moment `mount` has run; an external one is ready when its
   * frame has handshaked (§9.3). Both are moments *after* the launch call returns, so this waits
   * — briefly, and then gives up rather than holding a promise open forever.
   */
  async function deliverWhenReady(instanceKey: string, payload: Payload): Promise<boolean> {
    for (let attempt = 0; attempt < DELIVERY_ATTEMPTS; attempt += 1) {
      if (deliverPayload(instanceKey, payload)) return true;
      await new Promise((resolve) => setTimeout(resolve, DELIVERY_POLL_MS));
    }
    return false;
  }

  /**
   * §9.2 / §9.5 — open a path from a *shell* surface. A standalone function rather than only a
   * method, because §12's `Enter` on a file result is one of its callers and reaches it from
   * inside the kernel.
   */
  function openPath(path: VfsPath, appId?: string): string | null {
    const target = appId ?? filetypes.defaultFor(path);
    if (target === null || target === undefined) return null;
    const winId = launch.launch(target, { openPath: path });
    if (winId === null) return null;
    const opened = wm.get(winId);
    // The gesture that chose the file is the grant, exactly as a picker's would be (§9.5).
    if (opened) vfsGateway.grant(opened.instanceKey, [path]);
    return winId;
  }

  // ── §12 — the search index, and the four sources feeding it ────────────────────────────────
  //
  // Each source is refreshed by the events that can change it, and **only** by those: installing an
  // app must not cost a walk of the filesystem, and saving a file must not re-read the app
  // registry. That is what §12's "rebuilt incrementally" is for.
  const search = createSearchService();

  /** The `file` source, kept as a map so a single `fs:changed` costs one entry, not a walk. */
  const fileIndex = new Map<VfsPath, SearchEntry>();
  let filesDue: ReturnType<typeof setTimeout> | undefined;

  /**
   * Publish the file map. Debounced: an import or an unzip fires hundreds of events in a row, and
   * rebuilding the array for each one is work whose result is thrown away by the next.
   */
  function publishFiles(): void {
    clearTimeout(filesDue);
    filesDue = setTimeout(() => search.setSource('file', [...fileIndex.values()]), 200);
  }

  async function reindexFiles(): Promise<void> {
    const entries = await walkFiles(
      (dir) => vfs.list(dir),
      [userRoot(vfs.currentUser()), ROOT_APPS],
    );
    fileIndex.clear();
    for (const entry of entries) fileIndex.set(entry.id as VfsPath, entry);
    search.setSource('file', [...fileIndex.values()]);
  }

  const reindexApps = (): void => search.setSource('app', appEntries(launch.launchable()));
  const reindexWindows = (): void =>
    search.setSource('window', windowEntries(wm.list(), appNameOf));

  /**
   * Refresh the three cheap sources. Called when a launcher opens, for the same reason both
   * launchers already re-read `launchable()` there: the built-ins are registered *after* the
   * kernel is constructed, so anything computed at construction would be an empty list.
   */
  function reindexSearch(): void {
    reindexApps();
    reindexWindows();
    // §12's static source. Rebuilt with the others rather than once at construction, because it
    // costs a dozen entries and one code path is easier to be sure of than two.
    search.setSource('setting', settingEntries());
  }

  bus.on('app:registryChanged', reindexApps);
  bus.on('window:created', reindexWindows);
  bus.on('window:closed', reindexWindows);
  // A renamed window is a renamed search result: §9.3's `setTitle` is how an app names itself.
  bus.on('window:chromeChanged', reindexWindows);

  bus.on('fs:changed', (event) => {
    if (!indexable(event.path)) return;
    if (event.kind === 'delete') fileIndex.delete(event.path);
    else if (event.kind === 'move') {
      if (event.from) fileIndex.delete(event.from);
      if (event.entry) fileIndex.set(event.path, fileEntry(event.path, event.entry.name));
    } else if (event.entry) {
      fileIndex.set(event.path, fileEntry(event.path, event.entry.name));
    }
    publishFiles();
  });

  /**
   * §12 — "`Enter` launches or focuses".
   *
   * Which of the two depends on what was found, and that is the kernel's answer rather than each
   * launcher's: a file opens in its default app *with the click as its grant* (§9.5), a window is
   * restored and focused rather than opened a second time, and an app is launched. The MRU is
   * noted here too, so the boost follows what the user actually did in either skin.
   */
  function activateSearchResult(entry: SearchEntry): boolean {
    search.noteUse(entry.source, entry.id);
    switch (entry.source) {
      case 'app':
        return launch.launch(entry.id) !== null;
      case 'file':
        // The user picked it off a surface the shell drew, which is the gesture (§9.5, §19.4).
        return openPath(entry.id as VfsPath) !== null;
      case 'window': {
        const win = wm.get(entry.id);
        if (!win) return false;
        if (win.state === 'minimized') wm.restore(win.id);
        wm.focus(win.id);
        return true;
      }
      case 'setting':
        // §11.6's pages are not addressable until M13.7 completes Settings, so this opens the app
        // rather than pretending to deep-link (D-142).
        return launch.launch('settings') !== null;
      default:
        return false;
    }
  }

  /**
   * §13 — "reported back over `notify/action`". One delivery path for both transports, the same
   * shape and the same reason as `fireCommand` and `deliverPayload`: which transport a window
   * lives behind is the kernel's business and nobody else's.
   */
  function deliverNotifyAction(instanceKey: string, id: string, action: string): boolean {
    const connection = bridge.get(instanceKey);
    if (connection && connection.state === 'bridged') {
      return connection.send('notify/action', { id, action });
    }
    const handler = notifyActionHandlers.get(instanceKey);
    if (!handler) return false;
    handler(id, action);
    return true;
  }

  function fireCommand(instanceKey: string, commandId: string): boolean {
    const connection = bridge.get(instanceKey);
    if (connection && connection.state === 'bridged') {
      return connection.send('command/fire', { id: commandId });
    }
    return apps.fireCommand(instanceKey, commandId);
  }

  // §9.3 — the last edge: a validated inbound message becomes a call on the api host, which is the
  // same object the in-process transport binds to (D-006).
  bridgeRouter = createBridgeRouter({
    apiHost,
    winIdFor: (instanceKey) => wm.list().find((w) => w.instanceKey === instanceKey)?.id,
    onUnrouted: (connection, key) =>
      bus.emit('bridge:unrouted', { instanceKey: connection.instanceKey, key }),
    // §11.2 — a frame from a command the shell asked this app to run. Delivered to whoever asked
    // for *that* run: `to` is the request id, so two commands cannot cross into one tab (D-166).
    onExecFrame: (_connection, to, frame) => execListeners.get(to)?.(frame),
    /**
     * §9.7 step 3 — run it as if the shell had received the event itself.
     *
     * **Only if the shell claimed it.** §9.3 calls this "a forwarded *reserved* chord", and the
     * set is the shell's own: an app that sends anything else is not forwarding a key the user
     * pressed, it is pressing one they did not. That is dropped and counted, not obeyed.
     */
    onChord: (_connection, chord) => {
      if (!keyboard.reservedChords().includes(chord)) {
        bus.emit('bridge:drop', { reason: 'unreserved-chord' });
        return;
      }
      keyboard.dispatchChord(chord);
    },
  });
  // §9.5 — an `fs.watch` is the one thing on the bridge that outlives the call that made it, so it
  // ends where the window's picker grants end: on the same event, in the same breath.
  bus.on('window:closed', ({ instanceKey }) => bridgeRouter?.release(instanceKey));

  let immersive = false;
  /**
   * §34.4 — whether Quick Notes has armed its capture chord (D-059).
   *
   * A count rather than a flag: `armNotesCapture` is per registration, and a second registration
   * disarming the first would be a bug that only showed up in a session that had re-registered.
   */
  let notesCaptureArms = 0;
  const notesCaptureArmed = (): boolean => notesCaptureArms > 0;
  const keyboard = createKeyboardService({
    ...(options.platform ? { platform: options.platform } : {}),
    immersive: () => immersive,
    onBrowserReserved: () => shellui.showToast(COPY.stolenChord),
    // §9.7 step 4 — the claimed set changed, so every bridged app has to be re-told (D-130).
    onReservedChanged: (reservedChords) => bus.emit('keys:reservedChanged', { reservedChords }),
    dispatch: (action) => {
      const focused = wm.getState().focusedId;
      switch (action) {
        case 'launcher.open':
          shellui.toggleOverlay('launcher');
          break;
        case 'switcher.windows':
          // §6.4 hold-and-cycle: the first press opens it, every press after that advances the
          // selection. Toggling would close it on the second Tab, which is not what Alt-Tab is.
          if (shellui.activeOverlay() === 'switcher') {
            bus.emit('shell:switcherAdvance', { delta: 1 });
          } else {
            shellui.openOverlay('switcher');
          }
          break;
        case 'switcher.overview':
          shellui.toggleOverlay('overview');
          break;
        case 'clipboard.history':
          shellui.toggleOverlay('clipboard');
          break;
        case 'notifications.center':
          shellui.toggleOverlay('notifications');
          break;
        case 'shell.lock':
          shellui.openOverlay('lock');
          break;
        case 'shell.immersive':
          void toggleImmersive();
          break;
        case 'window.close':
          if (focused) void requestClose(focused);
          break;
        case 'app.explorer':
          launch.launch('explorer');
          break;
        case 'app.settings':
          launch.launch('settings');
          break;
        // §34.4 — the capture card, from anywhere. It reaches here rather than the focused app
        // because the shell's handler runs in the capture phase and a bridged app forwards
        // reserved chords instead of acting on them (§9.7) — which is the whole reason a game can
        // be in front and the card still appears. Unarmed, it does nothing at all: Quick Notes'
        // capability is what turns it on (D-059).
        case 'notes.capture': {
          if (!notesCaptureArmed()) break;
          const winId = launch.launch('notes', {
            forceNew: true,
            rect: centre({ w: NOTES_CAPTURE_WINDOW.width, h: NOTES_CAPTURE_WINDOW.height }, workArea),
            title: COPY.notes.captureTitle,
          });
          const key = winId === null ? undefined : wm.get(winId)?.instanceKey;
          // The card is *told* it is a card, on the same waiting delivery `launch.app`'s `arg` uses
          // (D-540): the window exists on the line above, and the app has not mounted yet.
          if (key !== undefined) {
            void deliverWhenReady(key, { items: [{ mime: NOTES_CAPTURE_MIME, text: '' }] });
          }
          break;
        }
        case 'desktop.previous':
          desktops.step(-1);
          break;
        case 'desktop.next':
          desktops.step(1);
          break;
        // §6.4 — `Meta+Arrow`. What an arrow *means* is the policy's, asked the same way an edge
        // drag asks it, so the house skin's up-is-full and the Windows skin's corners both come
        // out right without this knowing which skin is mounted (D-106).
        case 'window.snapLeft':
        case 'window.snapRight':
        case 'window.snapUp': {
          const id = wm.getState().focusedId;
          if (id === null) break;
          const edge = action === 'window.snapLeft' ? 'left' : action === 'window.snapRight' ? 'right' : 'top';
          snap.snapEdge(id, edge);
          break;
        }
        // Down is the way back out — a snapped or maximized window returns to its own rect, and
        // one that is already floating goes to the bar. That is what Win+Down does.
        case 'window.snapDown': {
          const id = wm.getState().focusedId;
          if (id === null) break;
          const win = wm.get(id);
          if (!win) break;
          if (win.state === 'snapped' || win.state === 'maximized') wm.restore(id);
          else wm.minimize(id);
          break;
        }
      }
    },
  });

  // §9.3 shell→app — the statements the shell makes to every bridged app about the desktop
  // around it, §9.7's re-sent chord set among them (D-130).
  attachBridgeEnv({
    bridge,
    bus,
    winOf: (winId) => wm.get(winId),
    activeSkin: () => settings.get('skin'),
  });

  async function toggleImmersive(): Promise<void> {
    const element = typeof document === 'undefined' ? null : document.documentElement;
    try {
      if (document?.fullscreenElement) {
        await document.exitFullscreen();
        immersive = false;
      } else {
        await element?.requestFullscreen();
        immersive = true;
      }
    } catch {
      // Fullscreen can be refused; immersive mode is an enhancement, never a blocker.
      immersive = Boolean(document?.fullscreenElement);
    }
  }

  registry.provide(BUS, () => bus);
  registry.provide(SETTINGS, () => settings);
  registry.provide(SOUND, () => sound);
  registry.provide(SHELLUI, () => shellui);
  // §26 — the three social services. Registered here rather than only held on the kernel object,
  // because §4.2's registry is what `serviceNames()` answers and §11.12's App Inspector reads:
  // a kernel service missing from that list is one nobody can see is running (M29.4).
  registry.provide(INVITES, () => invites);
  registry.provide(SOCIAL, () => social);
  registry.provide(CHAT, () => chat);
  registry.provide(PLAY, () => play);
  registry.provide(ALARMS, () => alarms);
  registry.provide(REMINDERS, () => reminders);
  registry.provide(WM, () => wm);
  registry.provide(APPS, () => apps);
  registry.provide(FILETYPES, () => filetypes);
  registry.provide(LAUNCH, () => launch);
  registry.provide(KEYBOARD, () => keyboard);
  registry.provide(OPFS, () => opfs);
  registry.provide(VFS, () => vfs);
  registry.provide(VERSIONS, () => versions);
  registry.provide(TRASH, () => trash);
  registry.provide(QUOTA, () => quota);
  registry.provide(EXPORTS, () => exports_);
  registry.provide(VFS_GATEWAY, () => vfsGateway);
  registry.provide(SNAP, () => snap);
  registry.provide(DESKTOPS, () => desktops);
  registry.provide(SESSION, () => session);
  registry.provide(TABS, () => tabs);

  /**
   * §11.2 — the exec providers: installed apps that both **hold** `exec.provide` and **declare**
   * commands. Both halves are required; a manifest listing commands without the permission
   * provides nothing, which is what makes the permission worth asking for (D-167).
   */
  const exec = createExecService({
    providers: () =>
      installed
        .list()
        .filter(
          (app) =>
            app.permissions.includes('exec.provide') &&
            (app.manifest.exec?.commands.length ?? 0) > 0,
        )
        .map((app) => ({
          appId: app.id,
          name: app.manifest.name,
          commands: app.manifest.exec?.commands ?? [],
        })),

    send: (appId, request, onFrame) => {
      // Any bridged window of that app will do — §11.2 delegates to the *app*, and an app with
      // three windows open is still one provider.
      const connection = bridge
        .connections()
        .find((candidate) => candidate.appId === appId && candidate.state === 'bridged');
      if (!connection) return null;

      const id = mintExecId();
      execListeners.set(id, onFrame);
      if (!connection.send('exec/run', { cmd: request.cmd, args: [...request.args], cwd: request.cwd }, id)) {
        execListeners.delete(id);
        return null;
      }
      return () => void execListeners.delete(id);
    },
  });


  /**
   * §7.4 — build the sync client on the real tree and turn the service on.
   *
   * Only ever called for a signed-in session. The client writes through the **internal** view
   * rather than the app gateway: these bytes come from the server and must land where the server
   * says, and §9.5's confinement exists to bound what an *app* may reach, not what sync may.
   */
  async function startSync(): Promise<void> {
    const state = createOpfsSyncState(vfs.internal);
    await state.hydrate();

    const client: SyncClient = createSyncClient({
      transport: createFetchSyncTransport(),
      state,
      files: createVfsSyncFiles(vfs.internal),
      sha256: sha256Hex,
      now: () => Date.now(),
      // §34.6 / D-496 — the device-local threshold, read from §14 on **every** call rather than
      // captured once. That is what makes changing the setting re-evaluate: the next run asks again,
      // and a file that was held back at 256 MB pushes at 1024 with nothing else touched.
      deviceLocal: (size) => isDeviceLocal(size, settings.get('media.syncThresholdMb')),
    });

    // The bookkeeping is flushed after each run rather than per entry: a run touches every path it
    // knows about, and a write each would be thousands of OPFS round trips for one sync.
    const persisting: SyncClient = {
      cursor: () => client.cursor(),
      markDirty: (path, sha) => client.markDirty(path, sha),
      markDeleted: (path) => client.markDeleted(path),
      markDirectory: (path) => client.markDirectory(path),
      sync: async () => {
        const result = await client.sync();
        await state.flush();
        return result;
      },
    };

    // Set *before* the first run: a write that lands mid-run still gets marked, and the debounce
    // picks it up. Left until after, a file saved during the opening sync would be invisible to
    // every later one.
    syncClient = persisting;
    sync.enable(persisting);

    // D-193 — §7.4's fifth trigger. Started *after* `enable`, because its first act may be to
    // ask for a run. Guests never reach here, which is what keeps them off the stream entirely.
    syncEvents?.stop();
    syncEvents = createSyncEvents({
      sync,
      cursorOf: () => persisting.cursor(),
      onStatus: (status) =>
        logs.log({
          source: 'files',
          // Only the loss is worth a warning: `retrying` means the desktop is back to §7.4's
          // poll, which is a degradation a user might otherwise never be told about.
          level: status === 'retrying' ? 'warn' : 'debug',
          message: `Change stream ${status}`,
        }),
    });
    syncEvents.start();

    await sync.run();
  }

  /** D-193 — closed on sign-out, so a signed-out tab does not hold a stream open. */
  function stopSync(): void {
    syncEvents?.stop();
    syncEvents = null;
    sync.disable();
  }


  return {
    registry,
    bus,
    settings,
    sound,
    shellui,
    invites,
    social,
    chat,
    openChatThread,
    onOpenChatThread(listener) {
      chatThreadListeners.add(listener);
      return () => chatThreadListeners.delete(listener);
    },
    pendingChatThread: () => {
      const peer = pendingChatThread;
      // Read once: it is a request, and a request that stayed readable would re-open the same
      // thread every time the window remounted.
      pendingChatThread = null;
      return peer;
    },
    play,
    async startPlay(gameId, peer) {
      // The window opens on the session the server minted, so the host is already in the game when
      // the invitee's card appears — which is what makes §26.4's "click to join" land somewhere.
      const session = await play.create(gameId, peer);
      openPlay(session.id, gameId);
    },
    async joinPlay(sessionId, gameId) {
      // Accept first: a window opened on an invitation nobody answered would be a game the server
      // will refuse every move for.
      await play.respond(sessionId, 'accept');
      openPlay(sessionId, gameId);
    },
    resumePlay(sessionId, gameId) {
      openPlay(sessionId, gameId);
    },
    livePlay: livePlaySessions,
    async activePlay(gameId) {
      const sessions = await livePlaySessions();
      return (
        sessions
          .filter((session) => session.gameId === gameId && session.state === 'active')
          .sort((a, b) => (b.activeAt ?? b.createdAt) - (a.activeAt ?? a.createdAt))[0] ?? null
      );
    },
    pendingPlay: () => {
      const request = pendingPlay;
      // Read once, for `pendingChatThread`'s reason.
      pendingPlay = null;
      return request;
    },
    onOpenPlay(listener) {
      playListeners.add(listener);
      return () => playListeners.delete(listener);
    },
    offers,

    /**
     * §34.13 — the roster picker, the offer, and the doorbell, in one call.
     *
     * `startPlay`'s shape: the shell does all three because only the shell can draw the picker
     * (§19.4) and only the shell may read the roster (§26.9). What comes back is an *outcome* —
     * Explorer never learns who was offered and never sees a ref.
     *
     * Presence is read alongside the roster so the picker can say who is actually around, and a
     * failure to read it is not a failure to send: everyone falls back to `offline`, which is what
     * `GET /api/social/presence` already answers for somebody who never checked in.
     */
    async sendFileToConnection(path, size) {
      if (identity().kind !== 'user') return { kind: 'failed' };

      let roster: Connection[];
      let presence: Presence[] = [];
      try {
        roster = await social.connections();
        presence = await social.presence().catch(() => []);
      } catch {
        return { kind: 'failed' };
      }

      const chosen = await shellui.pickConnection(
        {
          summary: COPY.offers.pickSummary(basename(path), formatOfferSize(size)),
          // §34.13 — "connections only". The rule is `rosterCandidates`' so it can be tested
          // without a kernel; it is the security-relevant half of this call.
          candidates: rosterCandidates(roster, presence),
        },
        'explorer',
      );
      if (chosen === null) return { kind: 'cancelled' };

      const outcome: SendOutcome = await offers.send(chosen, path);
      if (outcome.kind === 'sent') {
        return { kind: 'sent', toName: outcome.offer.toName, name: outcome.offer.name };
      }
      return { kind: outcome.kind };
    },

    /**
     * §34.13 — answer a card.
     *
     * **The card is removed by the refresh, not by the click** — the opposite of the game
     * invitation above, and §34.17 is why: a quota refusal has to leave the card exactly where it
     * was, so the recipient can free some space and press the same button. Removing it optimistically
     * would mean a refusal that also destroyed the only way to retry.
     */
    async answerFileOffer(id, answer) {
      try {
        if (answer === 'accept') {
          const accepted = await offers.accept(id);
          invites.withdrawFile(id);
          shellui.showToast(COPY.offers.accepted(accepted.offer.name));
        } else {
          await offers.decline(id);
          invites.withdrawFile(id);
        }
      } catch (error: unknown) {
        if (error instanceof OfferNoSpaceError) {
          // §34.13's binding literal, and the card stays up.
          shellui.showToast(COPY.offers.noSpace(formatOfferSize(error.size)));
          return;
        }
        shellui.showToast(COPY.offers.acceptFailed);
      } finally {
        // Whatever happened, the server is the truth about what is still live — including the case
        // where this tab's card was already answered in another one.
        void refreshOffers();
      }
    },
    reminders,
    alarms,
    calendarReminders,
    pendingCalendarDay: () => {
      const day = pendingCalendarDay;
      // Read once, like the chat thread: a second mount must not re-navigate to a day the user has
      // since moved away from.
      pendingCalendarDay = null;
      return day;
    },
    onOpenCalendarDay(listener) {
      calendarDayListeners.add(listener);
      return () => calendarDayListeners.delete(listener);
    },
    calendarChanged: () => void calendarReminders.check(),
    socialRef: () => socialRef,
    wm,
    apps,
    filetypes,
    launch,
    keyboard,
    opfs,
    vfs,
    versions,
    trash,
    desktop,
    quota,
    sync,
    exec,
    startSync,
    stopSync,
    installed,
    health,
    resume,
    bridge,
    menus,
    transfer,
    clipboard,
    drag,
    notify,
    logs,
    search,
    reindexFiles,
    reindexSearch,
    activateSearchResult,
    acceptsFor,
    fireCommand,
    deliverPayload,
    exports: exports_,
    vfsGateway,
    capabilities: browserCapabilities(),
    snap,
    desktops,
    session,
    tabs,
    policy: () => policy,
    setPolicy(next) {
      policy = next;
    },
    requestClose,
    apiHost,
    immersive: () => immersive,
    workArea: () => workArea,
    setWorkArea(rect) {
      workArea = { ...rect };
      // §5.2 (D-438) — the work area changing is the one moment a window can *become* unreachable
      // without anybody touching it: shrink the browser and a window that was fine is now under the
      // bar or off the side. This also runs at boot, which is what recovers a window a previous
      // session stranded above the top of the screen.
      wm.keepWindowsReachable(workArea);
    },
    identity,
    setIdentity(next, instance) {
      const wasSignedIn = account !== null;
      const wasUser = currentUser();
      account = next;
      // §26.1 — a ref is `<id>@<instance>` or it is nothing. A client that fell back to guessing
      // the hostname would namespace under a name the server does not use, and every social query
      // would quietly return nothing.
      socialRef =
        next === null || instance === null || instance === undefined
          ? null
          : `${next.id}@${instance}`;
      // §26.4 / §18 F15 — invitations belong to the session that received them.
      if (next === null) {
        invites.clear();
        social.stop();
        chatNotifier.reset();
        // §26.8 F15 — a game window is closed below, and the request that would reopen it goes with
        // it. An unsaved run's own blob is *not* touched: F15 says it is retained for the next
        // sign-in, and §24.8 already keeps it in the user's own space.
        pendingPlay = null;
      } else {
        chatNotifier.reset();
        social.start();
        // §26.4 (D-351) — a reload while an invitation was open lost the card, because the stack is
        // shell memory and the frame that filled it has already been and gone. One read puts back
        // everything still live.
        void refreshInvites();
        // §34.13 — the same read, for the same reason, and one more: this is the read that delivers
        // "{user} accepted {name}" to **the sender's next session**, which is a session that by
        // definition was not listening when the frame went out.
        void refreshOffers();
      }
      // §24.11 (D-200) — signing out closes any window whose app is now invisible. Leaving one
      // open would be a live gated app on a desktop that is no longer allowed to show it, and
      // closing it here rather than in the shell means every sign-out path gets the behaviour.
      if (wasSignedIn && next === null) {
        const allowed = new Set(apps.visible());
        for (const win of wm.list()) {
          if (apps.get(win.appId) !== undefined && !allowed.has(win.appId)) wm.close(win.id);
        }
      }
      // D-278 — signing in or out changes what `apps.visible()` answers (§24.11) and which user's
      // Desktop folder the desktop *is* (§7.1). Both are `app:registryChanged` consumers and
      // neither had any way to learn, because nothing on this path emitted: signing in mid-session
      // left §12's search index holding the guest's app list and the desktop watching
      // `/Users/guest`. Last, so a listener sees the settled state rather than a half-applied one.
      if (currentUser() !== wasUser) {
        // D-291 — and this user's settings are a different set from the last one's. Re-read them
        // from whichever half of §14 now applies: the server for a user, the device for a guest.
        void settings.hydrate().catch((error: unknown) => console.warn('settings', error));
        bus.emit('app:registryChanged', {});
      }
    },
    isAdmin: () => account?.isAdmin === true,
    resolvedTheme,
    activeSkin: () => settings.get('skin'),
    /**
     * An app's icon as an `<svg>` string, whatever its source. A manifest icon is a *URL*, so it
     * is wrapped in an svg rather than given a second rendering path — every skin already renders
     * one of these, and a second shape would mean editing all three (D-115).
     *
     * D-188: this **delegates** rather than repeating `appIconOf`. The two had been byte-identical
     * copies, which is a bug waiting to be written — the Share picker's comment already claimed
     * they were one function, and the moment the icon rule grew a third rung they would have
     * disagreed about what the launcher shows.
     */
    appIconSvg(appId, size = 32) {
      return appIconOf(appId, size);
    },

    appName: appNameOf,

    windowIconSvg(win, size = 32) {
      return win.iconSvg && win.iconSvg.length > 0
        ? win.iconSvg
        : this.appIconSvg(win.appId, size);
    },

    welcomeFor(appId) {
      const app = installed.get(appId);
      return {
        skin: settings.get('skin'),
        theme: resolvedTheme(),
        locale: typeof navigator === 'undefined' ? 'en' : navigator.language,
        user: identity(),
        // §9.7 — the chords the shell has claimed, in canonical form.
        reservedChords: keyboard.reservedChords(),
        permissions: [...(app?.permissions ?? [])],
      };
    },

    openPath,

    // §6.4 / §34.4 (D-544) — one writer for the override table. `keyboard.setOverrides` replaces it
    // whole, so the two settings that feed it are merged here rather than by two callers.
    applyKeyOverrides() {
      const chord = settings.get('notes.captureChord').trim();
      keyboard.setOverrides({
        ...settings.get('keymap.overrides'),
        // Empty means "the skin's own chord" (§34.1), so an empty setting must contribute *no* key
        // at all — an entry mapping to `''` would override the keymap with nothing.
        ...(chord.length > 0 ? { 'notes.capture': chord } : {}),
      });
    },

    armNotesCapture() {
      notesCaptureArms += 1;
      let released = false;
      return () => {
        // Idempotent: a React effect's cleanup can run twice under StrictMode, and a disarm that
        // decremented twice would leave the chord dead for the session.
        if (released) return;
        released = true;
        notesCaptureArms -= 1;
      };
    },
  };
}
